class ShopifyBundleContents extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.abortController = null;
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.list = this.querySelector('[data-bundle-components]');
    this.productForm = document.getElementById(this.dataset.formId);
    this.productForm?.addEventListener('variant:change', this.onVariantChange);
    this.renderVariant(this.dataset.variantId);
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener('variant:change', this.onVariantChange);
    this.abortController?.abort();
  }

  onVariantChange(event) {
    this.renderVariant(event.detail?.variant?.id);
  }

  async renderVariant(variantId) {
    if (!this.list || !this.dataset.storefrontAccessToken || !variantId) {
      this.hide();
      return;
    }

    const gid = String(variantId).startsWith('gid://')
      ? String(variantId)
      : `gid://shopify/ProductVariant/${variantId}`;

    if (this.currentVariantId === gid && this.classList.contains('is-loaded')) return;

    this.currentVariantId = gid;
    this.hidden = false;
    this.setAttribute('aria-busy', 'true');

    if (this.cache.has(gid)) {
      this.renderComponents(this.cache.get(gid));
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const components = await this.fetchComponents(gid, this.abortController.signal);
      this.cache.set(gid, components);
      if (this.currentVariantId === gid) this.renderComponents(components);
    }
    catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('[Shopify bundle contents]', error.message);
        this.hide();
      }
    }
  }

  async fetchComponents(variantGid, signal) {
    const response = await fetch(`${this.dataset.shopUrl}/api/${this.dataset.apiVersion || '2026-04'}/graphql.json`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.dataset.storefrontAccessToken
      },
      body: JSON.stringify({
        query: `
          query BundleComponents($variantId: ID!, $first: Int!) {
            node(id: $variantId) {
              ... on ProductVariant {
                components(first: $first) {
                  nodes {
                    quantity
                    productVariant {
                      image {
                        url(transform: { maxWidth: 160 })
                        altText
                        width
                        height
                      }
                      product {
                        title
                        handle
                        featuredImage {
                          url(transform: { maxWidth: 160 })
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          variantId: variantGid,
          first: Number(this.dataset.componentLimit || 30)
        }
      })
    });

    if (!response.ok) throw new Error(`Storefront API request failed with ${response.status}`);

    const payload = await response.json();
    if (payload.errors?.length) throw new Error(payload.errors[0].message);

    const node = payload?.data?.node;
    if (node?.components) return node.components.nodes || [];

    const storefrontVariantGid = await this.findStorefrontVariantGid(variantGid, signal);
    if (storefrontVariantGid && storefrontVariantGid !== variantGid) {
      return this.fetchComponents(storefrontVariantGid, signal);
    }

    throw new Error(`No Storefront API product variant found for ${variantGid}. Check that the product is published to the Storefront API channel for this token.`);
  }

  async findStorefrontVariantGid(variantGid, signal) {
    if (!this.dataset.productHandle) return null;

    const legacyId = variantGid.split('/').pop();
    const response = await fetch(`${this.dataset.shopUrl}/api/${this.dataset.apiVersion || '2026-04'}/graphql.json`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.dataset.storefrontAccessToken
      },
      body: JSON.stringify({
        query: `
          query ProductVariantIds($handle: String!) {
            product(handle: $handle) {
              variants(first: 100) {
                nodes {
                  id
                }
              }
            }
          }
        `,
        variables: {
          handle: this.dataset.productHandle
        }
      })
    });

    if (!response.ok) throw new Error(`Storefront API product lookup failed with ${response.status}`);

    const payload = await response.json();
    if (payload.errors?.length) throw new Error(payload.errors[0].message);

    const variants = payload?.data?.product?.variants?.nodes || [];
    return variants.find((variant) => variant.id === variantGid || variant.id.endsWith(`/${legacyId}`))?.id || null;
  }

  renderComponents(components) {
    if (!components.length) {
      this.hide();
      return;
    }

    if (this.hasAttribute('data-compact')) {
      this.renderCompact(components);
      return;
    }

    const fragment = document.createDocumentFragment();

    components.forEach((component) => {
      const product = component.productVariant?.product;
      if (!product?.title) return;

      const image = component.productVariant?.image || product.featuredImage;
      const quantity = Number(component.quantity || 1);
      const item = document.createElement('div');
      item.className = 'product-bundle__info product__shopify-bundle-content-item flex items-center gap-4 md:gap-6 relative';

      if (image?.url) {
        const picture = document.createElement('picture');
        picture.className = this.mediaClassName;

        const img = document.createElement('img');
        img.src = image.url;
        img.alt = image.altText || product.title;
        img.loading = 'lazy';
        if (image.width) img.width = image.width;
        if (image.height) img.height = image.height;

        picture.append(img);
        item.append(picture);
        item.classList.add('with-image');
      }

      const details = document.createElement('div');
      details.className = 'horizontal-product__details grow flex flex-col justify-start gap-1d5';

      const titleWrapper = document.createElement('div');
      titleWrapper.className = 'flex flex-col gap-1';

      const title = document.createElement('p');
      title.className = 'horizontal-product__title product-bundle__title product__shopify-bundle-content-title font-medium text-base leading-tight';
      if (product.handle) {
        const link = document.createElement('a');
        link.className = 'product__shopify-bundle-content-link';
        link.href = `${this.dataset.shopUrl}/products/${product.handle}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = `${quantity}x ${product.title}`;
        title.append(link);
      }
      else {
        title.textContent = `${quantity}x ${product.title}`;
      }
      titleWrapper.append(title);

      details.append(titleWrapper);
      item.append(details);

      fragment.append(item);
    });

    if (!fragment.childNodes.length) {
      this.hide();
      return;
    }

    this.list.replaceChildren(fragment);
    this.hidden = false;
    this.classList.add('is-loaded');
    this.removeAttribute('aria-busy');
  }

  renderCompact(components) {
    const fragment = document.createDocumentFragment();

    components.forEach((component) => {
      const product = component.productVariant?.product;
      if (!product?.title) return;

      const quantity = Number(component.quantity || 1);
      const item = document.createElement('li');
      item.className = 'droniq-cart-bundle__item';
      item.textContent = `${quantity}× ${product.title}`;
      fragment.append(item);
    });

    if (!fragment.childNodes.length) {
      this.hide();
      return;
    }

    this.list.replaceChildren(fragment);
    this.hidden = false;
    this.classList.add('is-loaded');
    this.removeAttribute('aria-busy');
  }

  hide() {
    this.hidden = true;
    this.classList.remove('is-loaded');
    this.removeAttribute('aria-busy');
  }

  get mediaClassName() {
    return this.dataset.mediaClass || 'product__shopify-bundle-content-media horizontal-product__media media media--square relative overflow-hidden shrink-0';
  }
}

if (!customElements.get('shopify-bundle-contents')) {
  customElements.define('shopify-bundle-contents', ShopifyBundleContents);
}
